import './styles.scss';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { createPortal } from "react-dom";
import { getParentByClass } from "../../../utils/utils";
import { useScreenSize } from '../../../hooks';

type ScrollModalViewProps = {
    children: React.ReactNode;
    header: React.ReactNode;
    onClose?: () => void;
}

type MousePosition = {
    x?: number;
    y?: number;
}

export type ScrollModalApi = {
    open: (e?: MousePosition) => void;
    close: () => void;
    isOpen?: boolean;
}

class OpenedModalInfo {
    private static _closeFn: () => void;
    private static _onClose?: () => void;

    public static close() {
        OpenedModalInfo._closeFn?.();
        OpenedModalInfo._onClose?.();
    }


    public static setIsCloseFn(closeFn: () => void) {
        OpenedModalInfo._closeFn = closeFn;
    }

}

const ScrollModalView = forwardRef<ScrollModalApi, ScrollModalViewProps>(({ children, header, onClose }, ref) => {

    const [isOpen, setIsOpen] = React.useState(false);
    const startY = useRef(0);
    const lastY = useRef(0);
    const endY = useRef(0);
    const isSwipeBottom = useRef(false);
    const scrollModalRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const { width, height } = useScreenSize();
    const [position, setPosition] = React.useState<MousePosition | null>(null);

    const handleClose = () => {
        setIsOpen(false);
        onClose && onClose();
        if (!scrollModalRef.current) return;
        scrollModalRef.current!.style.bottom = '';
    }

    const handleOpen = (e?: MousePosition) => {
        OpenedModalInfo.close();
        setIsOpen(true);
        OpenedModalInfo.setIsCloseFn(handleClose);

        if (e?.x == null || e?.y == null) {
            return;
        }

        const scrollModalWidth = scrollModalRef.current?.clientWidth || 0;
        const scrollModalHeight = scrollModalRef.current?.clientHeight || 0;
        const scrollPadding = 15;

        let posX = e.x;
        if (posX + scrollModalWidth > width) {
            posX = posX - scrollModalWidth - scrollPadding;
        } else if (posX < 0) {
            posX = scrollPadding;
        } else {
            posX = posX + scrollPadding;
        }
        if (posX < 0) {
            posX = scrollPadding;
        }

        scrollModalRef.current?.style.setProperty('--left', `${posX}px`);

        if (e.y + scrollModalHeight > height) {
            scrollModalRef.current?.style.setProperty('--top', 'unset');
            scrollModalRef.current?.style.setProperty('--bottom', '0px');
        } else {
            const posY = e.y < 0 ? scrollPadding : e.y + scrollPadding;
            scrollModalRef.current?.style.setProperty('--top', `${posY}px`);
            scrollModalRef.current?.style.setProperty('--bottom', 'unset');
        }
    }

    useImperativeHandle(ref, () => ({
        open: handleOpen,
        close: handleClose,
        isOpen
    }));

    useEffect(() => {
        if (!scrollModalRef.current) return;
        if (width > 1200) return;

        const handleSwipeDown = (e: TouchEvent) => {
            if (startY.current === 0) return;


            const modalBody = getParentByClass(e.target as HTMLElement, 'scroll-modal__body');
            if (modalBody && modalBody.scrollTop > 0) {
                startY.current = 0;
                scrollModalRef.current!.style.bottom = `0px`;
                return;
            }

            e.stopPropagation();

            scrollModalRef.current!.classList.add('scroll-modal__deanimate-bottom');
            endY.current = Number(e.touches[0].clientY);
            scrollModalRef.current!.style.bottom = `${Math.min(0, 0 - (endY.current - startY.current))}px`;
            isSwipeBottom.current = endY.current > lastY.current || (endY.current === lastY.current && isSwipeBottom.current);
            lastY.current = Number(endY.current);
        };

        const handleTouchStart = (e: TouchEvent) => {

            const modalBody = getParentByClass(e.target as HTMLElement, 'scroll-modal__body');
            if (modalBody && modalBody.scrollTop > 0) return;

            startY.current = Number(e.touches[0].clientY);
            lastY.current = Number(e.touches[0].clientY);
            endY.current = Number(e.touches[0].clientY);
            isSwipeBottom.current = false;
            scrollModalRef.current!.classList.add('scroll-modal__deanimate-bottom');
        }

        const handleTouchEnd = (e: TouchEvent) => {
            if (startY.current === 0) return;
            startY.current = 0;
            scrollModalRef.current!.classList.remove('scroll-modal__deanimate-bottom');
            if (isSwipeBottom.current) {
                handleClose()
            } else {
                scrollModalRef.current!.style.bottom = `0`;
            }
        }

        scrollModalRef.current.addEventListener('touchstart', handleTouchStart, { passive: true });
        scrollModalRef.current.addEventListener('touchend', handleTouchEnd, { passive: true })
        scrollModalRef.current.addEventListener('touchmove', handleSwipeDown, { passive: true });

        return () => {
            scrollModalRef.current?.removeEventListener('touchstart', handleTouchStart);
            scrollModalRef.current?.removeEventListener('touchend', handleTouchEnd);
            scrollModalRef.current?.removeEventListener('touchmove', handleSwipeDown);
        }
    }, [scrollModalRef.current, width]);
    useEffect(() => {
        if (!bodyRef.current || !headerRef.current || !scrollModalRef.current) return;

        const contentHeight = bodyRef.current.querySelector('.scroll-modal__body__content')?.clientHeight || 0;
        const headerHeight = headerRef.current.clientHeight;

        bodyRef.current.style.height = `min(${contentHeight + 5}px, calc(${window.innerHeight * 0.9}px - 22px - ${headerHeight}px))`;


    }, [children]);

    useEffect(() => {
        if (!isOpen) return;
        const handlePressEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                e.preventDefault();
                handleClose();
            }
        }
        document.addEventListener('keydown', handlePressEsc);
        return () => {
            document.removeEventListener('keydown', handlePressEsc);
        }
    }, [handleClose, isOpen]);

    useEffect(() => {
        if (!scrollModalRef.current) return;

        if (!isOpen) return;

        if (width < 1200) {
            return;
        }

        const handleClickDocument = (e: MouseEvent) => {
            if (scrollModalRef.current?.contains(e.target as Node)) {
                return;
            }
            console.log('closing modal');
            handleClose();
        };

        document.addEventListener('pointerdown', handleClickDocument);
        document.addEventListener('wheel', handleClickDocument);
        return () => {
            document.removeEventListener('pointerdown', handleClickDocument);
            document.removeEventListener('wheel', handleClickDocument);
        }

    }, [width, isOpen]);


    const handleBodyModalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    }

    return createPortal(
        <>
            {width <= 1200 && <div className={"scroll-modal__backdrop " + (!isOpen && "scroll-modal__backdrop-closed")}
                onClick={handleClose}>

            </div>}
            <div className={"scroll-modal " + (!isOpen && "scroll-modal__modal-closed")}
                onScroll={e => e.stopPropagation()}
                ref={scrollModalRef}>
                {width <= 1200 && <>
                    <div className="scroll-modal__icon-scroll">
                        <span></span>
                    </div>
                    <div ref={headerRef} className="scroll-modal__header">
                        {header}
                    </div>
                    <hr className="scroll-modal__header__separator" />
                </>}
                <div ref={bodyRef} onScroll={handleBodyModalScroll} className="scroll-modal__body">
                    <div className="scroll-modal__body__content">
                        {children}
                    </div>
                </div>
            </div>
        </>,
        document.body
    )
});

export default ScrollModalView;
